import { Dialog } from '@headlessui/react';
import { X, CheckCircle2, Clock, FileText, Download } from 'lucide-react';
import type { AssetAssignment } from '../api';
import { useAssignments, useDownloadDocumento } from '../hooks';
import { formatarData } from '../utils/formatters';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
}

export function HistoricoResponsaveisDrawer({ open, assetId, onClose }: Props) {
  const query = useAssignments(open ? assetId : undefined);
  const downloadMutation = useDownloadDocumento(assetId);

  async function handleDownloadTerm(documentId: string | null) {
    if (!documentId) return;
    try {
      await downloadMutation.mutateAsync(documentId);
    } catch { /* toast futuro */ }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <Dialog.Panel className="flex h-full w-screen max-w-md flex-col bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Histórico de responsáveis
            </Dialog.Title>
            <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {query.isLoading && (
              <div className="animate-pulse space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-24 rounded bg-gray-100" />
                ))}
              </div>
            )}
            {query.data?.data.length === 0 && (
              <p className="text-center text-sm text-gray-500 py-10">
                Nenhum vínculo registrado para este ativo.
              </p>
            )}
            <ol className="space-y-4">
              {query.data?.data.map((a: AssetAssignment) => (
                <li key={a.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {a.custodian?.name ?? '(responsável removido)'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatarData(a.deliveredAt)} → {a.returnedAt ? formatarData(a.returnedAt) : <span className="font-medium text-green-700">atual</span>}
                      </p>
                    </div>
                    {a.signatureStatus === 'signed' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">
                        <CheckCircle2 className="h-3 w-3" /> Assinado
                      </span>
                    ) : a.signatureStatus === 'pending' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                        <Clock className="h-3 w-3" /> Pendente
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">
                        Não requer
                      </span>
                    )}
                  </div>
                  {a.returnReason && (
                    <p className="mt-2 text-xs text-gray-600">
                      <strong>Motivo:</strong> {a.returnReason}
                    </p>
                  )}
                  {a.signedAt && (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Assinado em {formatarData(a.signedAt)}
                      {a.signedOnBehalfOfCustodian
                        ? ` por operador em nome de ${a.signedOnBehalfOfCustodian.name}`
                        : a.signedByUser
                          ? ` por ${a.signedByUser.name}`
                          : ''}
                    </p>
                  )}
                  {a.responsibilityTermId && (
                    <button
                      onClick={() => handleDownloadTerm(a.responsibilityTermId)}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    >
                      <FileText className="h-3 w-3" />
                      <span>{a.termNumber ?? 'Termo'}</span>
                      <Download className="h-3 w-3" />
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
